/**
 * O histórico de atividades do modal do Negócio (11.4).
 *
 * Compõe **duas leituras do próprio `negocios`** — as notas (`GET /negocios/{id}/notas`) e as
 * tarefas do Negócio (`GET /tarefas?negocio_id=`) — em cartões intercalados, em ordem cronológica
 * inversa, com as **notas fixadas no topo** (entre elas, a mesma ordem). A composição é do SPA
 * (AD-25), sem rota agregadora. Mensagem de WhatsApp não entra (a conversa tem tela própria), e as
 * movimentações têm cartão próprio — nunca viram cartão de atividade.
 *
 * Ações desta história: fixar/desafixar (nota — quem vê, fixa), editar e excluir (nota — só quando
 * o serviço diz `editavel` e, desde a 13.14b, `excluivel`: autor ou escopo `todas`), e **Concluir**
 * (tarefa pendente — o "Finalizar" do protótipo v2 é esta mesma ação, no modal estreito de sempre). Editar/excluir tarefa é da 11.6.
 */

import { type MouseEvent, useEffect, useMemo, useRef, useState } from 'react'

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import { ConclusaoDeTarefa } from './ConclusaoDeTarefa'
import { Dialogo } from './Dialogo'
import { DialogoDaTarefa } from './DialogoDaTarefa'
import {
  type Nota,
  type SugestaoDeProximoPasso,
  type Tarefa,
  TAMANHO_DO_TEXTO_DA_NOTA,
  useEditarNota,
  useExcluirNota,
  useFixarNota,
  useNotas,
  urlDoAnexo,
  useExcluirTarefa,
  useTarefas,
} from './api'
import { DATA_E_HORA, PRAZO_VENCIDO, prazoPorExtenso } from './apresentacao'
import { marcaDaConclusao } from './regras-de-tarefa'
import './historico-de-atividades.css'
import './tarefas-do-negocio.css'

/** O estado vazio da espinha (State Patterns). */
export const HISTORICO_VAZIO = 'Nada registrado ainda. A primeira nota ou tarefa entra aqui.'
export const CARREGANDO_O_HISTORICO = 'Carregando o histórico…'
export const TAREFA_CONCLUIDA_NO_HISTORICO = 'Tarefa concluída.'

function mensagemDe(falha: unknown): string {
  return falha instanceof ErroDaApi ? falha.message : MENSAGEM_DE_ERRO_DESCONHECIDO
}

type Atividade =
  | { tipo: 'nota'; quando: string; fixada: boolean; nota: Nota }
  | { tipo: 'tarefa'; quando: string; fixada: false; tarefa: Tarefa }

/**
 * A ordem do histórico: fixadas primeiro; depois, da mais nova para a mais antiga pela criação.
 * Exportada porque é regra pura, e a suíte a observa sem montar a tela.
 */
export function ordenarAtividades(notas: readonly Nota[], tarefas: readonly Tarefa[]): Atividade[] {
  const atividades: Atividade[] = [
    ...notas.map((nota) => ({
      tipo: 'nota' as const,
      quando: nota.criado_em,
      fixada: nota.fixada,
      nota,
    })),
    ...tarefas.map((tarefa) => ({
      tipo: 'tarefa' as const,
      quando: tarefa.criado_em,
      fixada: false as const,
      tarefa,
    })),
  ]
  return atividades.sort((uma, outra) => {
    if (uma.fixada !== outra.fixada) {
      return uma.fixada ? -1 : 1
    }
    return Date.parse(outra.quando) - Date.parse(uma.quando)
  })
}

export interface PropsDoHistorico {
  negocioId: string
  /** O nome de quem escreveu a nota ou é Responsável pela tarefa — resolvido pela ficha. */
  nomeDaPessoa: (id: string) => string
  /** A Tarefa concluída sugeriu um próximo passo e a pessoa aceitou (4.5, FR-21). */
  aoAceitarASugestao?: (tarefa: Tarefa, sugestao: SugestaoDeProximoPasso) => void
  /** A sugestão foi recusada: o foco vai ao compositor, porque o botão Concluir já sumiu. */
  aoRecusarASugestao?: () => void
  /** Avisa o modal quando uma nota está em edição (conta como alteração pendente ao fechar). */
  aoMudarPendencia?: (pendente: boolean) => void
  /** Desliga as leituras (o Negócio acabou de ser excluído). */
  habilitado?: boolean
  /**
   * 13.14c (DW-527): quem lê o Negócio só pela tarefa aberta (o `somente_leitura` da ficha). As
   * tarefas de outra pessoa (nem Responsável nem criadora) ficam sem Concluir, Editar e Excluir — a
   * policy da Tarefa as recusaria; as próprias continuam.
   */
  somenteLeitura?: boolean
  /** O `usuario_id` de quem lê — o da sessão. */
  quemLe?: string
}

export function HistoricoDeAtividades({
  negocioId,
  nomeDaPessoa,
  aoAceitarASugestao,
  aoRecusarASugestao,
  aoMudarPendencia,
  habilitado = true,
  somenteLeitura = false,
  quemLe,
}: PropsDoHistorico) {
  const notas = useNotas(negocioId, { habilitada: habilitado })
  const tarefas = useTarefas({ negocioId }, { habilitada: habilitado })
  const fixar = useFixarNota(negocioId)
  const editar = useEditarNota(negocioId)
  const excluir = useExcluirNota(negocioId)

  const [editando, setEditando] = useState<{ id: string; texto: string } | null>(null)
  const emEdicao = editando !== null
  useEffect(() => {
    aoMudarPendencia?.(emEdicao)
  }, [emEdicao, aoMudarPendencia])
  const [excluindo, setExcluindo] = useState<Nota | null>(null)
  const [concluindo, setConcluindo] = useState<Tarefa | null>(null)
  const [concluida, setConcluida] = useState(false)
  //: 11.6 (FR-80): editar e excluir a Tarefa pelo cartão — concluída inclusive.
  const [editandoTarefa, setEditandoTarefa] = useState<Tarefa | null>(null)
  const [excluindoTarefa, setExcluindoTarefa] = useState<Tarefa | null>(null)
  const excluirTarefa = useExcluirTarefa()
  const gatilho = useRef<HTMLElement | null>(null)

  const atividades = useMemo(
    () => ordenarAtividades(notas.data ?? [], tarefas.data ?? []),
    [notas.data, tarefas.data],
  )

  /* As ações da Tarefa (13.14c): todas, fora do só leitura; nele, só as da própria (Responsável ou
     criadora — a `EXPRESSAO_DE_EDICAO_DA_TAREFA` do serviço). */
  const acoesDa = (tarefa: Tarefa): boolean =>
    !somenteLeitura || tarefa.responsavel_id === quemLe || tarefa.criador_id === quemLe

  const devolverOFoco = () => {
    const elemento = gatilho.current
    gatilho.current = null
    elemento?.focus()
  }

  const lembrarGatilho = (evento: MouseEvent<HTMLButtonElement>) => {
    gatilho.current = evento.currentTarget
  }

  let corpo
  if (notas.isError || tarefas.isError) {
    corpo = (
      <div className="ficha__recusa">
        <p className="ficha__erro" role="alert">
          {mensagemDe(notas.isError ? notas.error : tarefas.error)}
        </p>
        <button
          type="button"
          className="ficha__botao"
          onClick={() => {
            void notas.refetch()
            void tarefas.refetch()
          }}
        >
          Tentar de novo
        </button>
      </div>
    )
  } else if (notas.data === undefined || tarefas.data === undefined) {
    corpo = (
      <p className="ficha__vazio" role="status">
        {CARREGANDO_O_HISTORICO}
      </p>
    )
  } else if (atividades.length === 0) {
    corpo = <p className="ficha__vazio">{HISTORICO_VAZIO}</p>
  } else {
    corpo = (
      <ul className="historico" aria-label="Atividades do negócio">
        {atividades.map((atividade) =>
          atividade.tipo === 'nota' ? (
            <li
              key={`nota-${atividade.nota.id}`}
              className="historico__cartao"
              data-tipo="nota"
              data-fixada={atividade.nota.fixada}
            >
              <div className="historico__topo">
                <span className="tarefas__tag">Nota</span>
                {atividade.nota.fixada ? <span className="historico__fixada">Fixada</span> : null}
                <span className="historico__meta">
                  {nomeDaPessoa(atividade.nota.autor_id)} ·{' '}
                  <span className="numero">
                    {DATA_E_HORA.format(new Date(atividade.nota.criado_em))}
                  </span>
                </span>
              </div>
              {editando?.id === atividade.nota.id ? (
                <form
                  className="historico__edicao"
                  onSubmit={(evento) => {
                    evento.preventDefault()
                    const texto = editando.texto.trim()
                    if (texto === '') {
                      return
                    }
                    editar.mutate(
                      { notaId: atividade.nota.id, texto },
                      {
                        onSuccess: () => {
                          setEditando(null)
                        },
                      },
                    )
                  }}
                >
                  <label className="ficha__so-para-leitor" htmlFor={`nota-${atividade.nota.id}`}>
                    Texto da nota
                  </label>
                  <textarea
                    id={`nota-${atividade.nota.id}`}
                    className="ficha__entrada ficha__entrada--longa"
                    value={editando.texto}
                    maxLength={TAMANHO_DO_TEXTO_DA_NOTA}
                    onChange={(evento) => {
                      setEditando({ id: atividade.nota.id, texto: evento.target.value })
                    }}
                  />
                  <div className="historico__acoes">
                    <button
                      type="button"
                      className="ficha__botao"
                      onClick={() => {
                        setEditando(null)
                        editar.reset()
                      }}
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="ficha__botao ficha__botao--primario"
                      disabled={editar.isPending || editando.texto.trim() === ''}
                    >
                      Salvar
                    </button>
                  </div>
                  {editar.isError ? (
                    <p className="ficha__erro" role="alert">
                      {mensagemDe(editar.error)}
                    </p>
                  ) : null}
                </form>
              ) : (
                <p className="historico__texto">{atividade.nota.texto}</p>
              )}
              {editando?.id === atividade.nota.id ? null : (
                <div className="historico__acoes">
                  {/* 13.14c (DW-527): Fixar pelo `fixavel` do serviço ("quem vê, fixa" — menos a
                      PA-3). Sem a chave (o serviço de antes do deploy), como antes. */}
                  {atividade.nota.fixavel === false ? null : (
                    <button
                      type="button"
                      className="historico__acao"
                      disabled={fixar.isPending}
                      onClick={() => {
                        fixar.mutate({
                          notaId: atividade.nota.id,
                          fixada: !atividade.nota.fixada,
                        })
                      }}
                    >
                      {atividade.nota.fixada ? 'Desafixar' : 'Fixar'}
                    </button>
                  )}
                  {atividade.nota.editavel ? (
                    <button
                      type="button"
                      className="historico__acao"
                      onClick={() => {
                        editar.reset()
                        setEditando({ id: atividade.nota.id, texto: atividade.nota.texto })
                      }}
                    >
                      Editar
                    </button>
                  ) : null}
                  {/* 13.14b (DW-446): Excluir pelo `excluivel` próprio — a autora exclui a
                      própria nota também no Negócio que vê pelo Grupo ou pela PA-3. */}
                  {atividade.nota.excluivel ? (
                    <button
                      type="button"
                      className="historico__acao historico__acao--perigo"
                      onClick={(evento) => {
                        lembrarGatilho(evento)
                        excluir.reset()
                        setExcluindo(atividade.nota)
                      }}
                    >
                      Excluir
                    </button>
                  ) : null}
                </div>
              )}
            </li>
          ) : (
            <li
              key={`tarefa-${atividade.tarefa.id}`}
              className="historico__cartao"
              data-tipo="tarefa"
            >
              <div className="historico__topo">
                <span className="tarefas__tag">{atividade.tarefa.tipo_nome}</span>
                <span className="historico__meta">
                  {nomeDaPessoa(atividade.tarefa.responsavel_id)}
                </span>
              </div>
              <p className="historico__texto">{atividade.tarefa.titulo}</p>
              {atividade.tarefa.descricao ? (
                <p className="historico__texto historico__texto--descricao">
                  {atividade.tarefa.descricao}
                </p>
              ) : null}
              {atividade.tarefa.anexos.length > 0 ? (
                /* O arquivo só pela rota da Tarefa, com a visibilidade dela (AD-9) — nunca URL
                   pública. */
                <ul className="historico__anexos" aria-label="Anexos da tarefa">
                  {atividade.tarefa.anexos.map((anexo) => (
                    <li key={anexo.id}>
                      <a
                        href={urlDoAnexo(atividade.tarefa.id, anexo.id)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {anexo.nome_original}
                      </a>
                    </li>
                  ))}
                </ul>
              ) : null}
              <div className="historico__acoes">
                {atividade.tarefa.concluida_em === null ? (
                  <>
                    <span className="tarefas__prazo" data-vencida={atividade.tarefa.vencida}>
                      {prazoPorExtenso(atividade.tarefa.prazo)}
                      {atividade.tarefa.vencida ? (
                        <span className="ficha__so-para-leitor">{PRAZO_VENCIDO}</span>
                      ) : null}
                    </span>
                    {acoesDa(atividade.tarefa) ? (
                      <button
                        type="button"
                        className="tarefas__acao"
                        onClick={(evento) => {
                          lembrarGatilho(evento)
                          setConcluida(false)
                          setConcluindo(atividade.tarefa)
                        }}
                      >
                        Concluir
                      </button>
                    ) : null}
                  </>
                ) : (
                  <span className="tarefas__concluida">
                    {marcaDaConclusao(
                      atividade.tarefa.concluida_em,
                      atividade.tarefa.resultado_nome,
                    )}
                  </span>
                )}
                {acoesDa(atividade.tarefa) ? (
                  <>
                    <button
                      type="button"
                      className="tarefas__acao"
                      aria-label={`Editar: ${atividade.tarefa.titulo}`}
                      onClick={(evento) => {
                        lembrarGatilho(evento)
                        setEditandoTarefa(atividade.tarefa)
                      }}
                    >
                      Editar
                    </button>
                    <button
                      type="button"
                      className="tarefas__acao"
                      aria-label={`Excluir: ${atividade.tarefa.titulo}`}
                      onClick={(evento) => {
                        lembrarGatilho(evento)
                        excluirTarefa.reset()
                        setExcluindoTarefa(atividade.tarefa)
                      }}
                    >
                      Excluir
                    </button>
                  </>
                ) : null}
              </div>
            </li>
          ),
        )}
      </ul>
    )
  }

  return (
    <section className="ficha__card ficha__card--solto" aria-label="Histórico de atividades">
      {/* O título como no protótipo v2: fora de caixa, com a contagem à direita. */}
      <div className="ficha__card-topo">
        <span className="ficha__card-titulo">Histórico de atividades</span>
        <span className="ficha__card-nota">
          {`${String(
            atividades.filter((a) => a.tipo === 'tarefa' && a.tarefa.concluida_em === null).length,
          )} tarefa(s) aberta(s) · ${String(atividades.filter((a) => a.tipo === 'nota').length)} nota(s)`}
        </span>
      </div>
      <div className="ficha__card-corpo">
        {corpo}
        {fixar.isError ? (
          <p className="ficha__erro" role="alert">
            {mensagemDe(fixar.error)}
          </p>
        ) : null}
        {concluida ? (
          <p className="ficha__salvo" role="status">
            {TAREFA_CONCLUIDA_NO_HISTORICO}
          </p>
        ) : null}
      </div>

      {excluindo === null ? null : (
        <Dialogo
          titulo="Excluir nota"
          subtitulo="A nota sai do histórico e não volta."
          rotuloDoConfirmar="Excluir nota"
          ocupado={excluir.isPending}
          erro={excluir.isError ? excluir.error : undefined}
          aoFechar={() => {
            setExcluindo(null)
            devolverOFoco()
          }}
          aoConfirmar={() => {
            excluir.mutate(excluindo.id, {
              onSuccess: () => {
                setExcluindo(null)
              },
            })
          }}
        >
          <p className="ficha__dialogo-aviso">{excluindo.texto}</p>
        </Dialogo>
      )}

      {editandoTarefa === null ? null : (
        <DialogoDaTarefa
          tarefa={editandoTarefa}
          aoFechar={() => {
            setEditandoTarefa(null)
            devolverOFoco()
          }}
        />
      )}

      {excluindoTarefa === null ? null : (
        <Dialogo
          titulo="Excluir tarefa"
          subtitulo="A tarefa e os anexos dela deixam de existir. A trilha de auditoria registra a exclusão."
          rotuloDoConfirmar="Excluir tarefa"
          ocupado={excluirTarefa.isPending}
          erro={excluirTarefa.isError ? excluirTarefa.error : undefined}
          aoFechar={() => {
            if (excluirTarefa.isPending) {
              return
            }
            setExcluindoTarefa(null)
            devolverOFoco()
          }}
          aoConfirmar={() => {
            excluirTarefa.mutate(excluindoTarefa.id, {
              onSuccess: () => {
                setExcluindoTarefa(null)
                gatilho.current = null
              },
            })
          }}
        >
          <p className="ficha__dialogo-aviso">{excluindoTarefa.titulo}</p>
        </Dialogo>
      )}

      <ConclusaoDeTarefa
        concluindo={concluindo}
        aoFechar={() => {
          setConcluindo(null)
          devolverOFoco()
        }}
        aoConcluir={() => {
          setConcluida(true)
        }}
        aoAceitarASugestao={(tarefa, sugestao) => {
          aoAceitarASugestao?.(tarefa, sugestao)
        }}
        aoRecusarASugestao={() => {
          aoRecusarASugestao?.()
        }}
      />
    </section>
  )
}
